// ** Redux Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { getErrorMessage } from '@/utils/errors';
import urlonStringify from '@/utils/urlon';

import { RootState } from '../';

const DEBUG = false;

type FetchConfigProps = {
  ids: string[];
};

type FetchConfigResult = {
  data: any[];
  success: boolean;
};

type FetchConfigError = {
  rejectValue: {
    error: string;
  };
};

const apiUrl = window.config.dataSourceApiUrl;

export const fetchElements = createAsyncThunk<FetchConfigResult, FetchConfigProps, FetchConfigError>(
  'fetchElements/query',
  async ({ ids }, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState() as RootState;

      const authToken = window.config.guestToken ? window.config.guestToken : '';

      const response = await axios.get(`${apiUrl}/resolve/elements`, {
        headers: {
          Authorization: 'Bearer ' + authToken,
        },
        params: {
          q: urlonStringify({ ids }),
        },
        // Guest-token read, never the owner session: a 401 here is never an owner-session expiry, so opt out
        // of the global auth recovery/teardown (mirrors the fetchSources sibling) instead of signing out.
        _owsNoAuthRecovery: true,
      });

      if (response.data) {
        return { data: response.data, success: true };
      }

      return rejectWithValue({
        error: "Couldn't fetch elements",
      });
    } catch (error) {
      return rejectWithValue({
        error: getErrorMessage(error),
      });
    }
  },
);

export interface ConfigReducer {
  items: any;
  loading: boolean;
  error: any;
}

export const dataElementsSlice = createSlice({
  name: 'dataElements',
  initialState: <ConfigReducer>{
    items: {},
    loading: false,
    error: null,
  },
  reducers: {
    clearDataElements: (state) => {
      state.items = {};
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchElements.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.items = { ...state.items, ...action.payload.data };
        }
        DEBUG && console.timeEnd('fetchElements.query');
        DEBUG && console.log('fetchElements.fulfilled', action.payload);
      })
      .addCase(fetchElements.pending, (state) => {
        DEBUG && console.time('fetchElements.query');
        state.loading = true;
      })
      .addCase(fetchElements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error;
        DEBUG && console.log('fetchElements.rejected', action.payload);
      });
  },
});

export const { clearDataElements } = dataElementsSlice.actions;

export default dataElementsSlice.reducer;
